import { match } from "ts-pattern";

import { dayjs } from "@thalamos/common";
import {
  showsInSummary,
  type AssessmentId,
  type FinishedAssessment,
  type GroupCategory,
  type PersonInvolvement,
  type RatedItem,
  type RatingScale,
} from "@thalamos/haven-common";

import { familyOfName } from "../Assessment/comparisonCandidates.js";

/** Which items the history's table can list, in the order they are offered. */
export const HISTORY_ITEMS = ["current", "historic", "all"] as const;

export type HistoryItems = (typeof HISTORY_ITEMS)[number];

/** The kinds of domain the progression chart draws, one line each. */
export const CHARTED = ["needs", "strengths", "history"] as const;

export type Charted = (typeof CHARTED)[number];

/** One approved assessment in the history, read and laid out by what it rated. */
export type HistoryPoint = {
  assessmentId: AssessmentId;
  at: Date;
  /** Every item it rated with a number. */
  rated: readonly RatedItem[];
  /** Undefined where who it was is not known. */
  assessor: string | undefined;
};

/** How an item went across every assessment in the range. */
export type AcrossRange =
  | "not_rated"
  | "same"
  | "changed"
  | "persistent"
  | "new"
  | "recurring"
  | "resolved"
  | "still_to_build"
  | "built";

export type HistoryRow = {
  item: string;
  title: string;
  category: GroupCategory;
  scale: RatingScale;
  /** Its rating in each assessment of the range, oldest first; undefined where one did not rate it. */
  ratings: (number | undefined)[];
  /** Absent where the range holds one assessment, which has nothing to go by. */
  across?: AcrossRange | undefined;
};

export type HistoryRowGroup = {
  key: string;
  category: GroupCategory;
  /** The domain's title, for needs, which are grouped by domain; absent for the others, grouped by kind. */
  domain?: string | undefined;
  rows: HistoryRow[];
};

/** Something that happened to the young person between assessments. */
export type HistoryEvent =
  | { kind: "placement"; at: Date; name: string }
  | { kind: "assessor"; at: Date; name: string | undefined };

/** One family's approved assessments the history may show. */
export type HistoryFamily = {
  family: string;
  count: number;
  latest: Date;
};

/** The day an instant falls on, as an address names it. */
export const dayOf = (at: Date): string => dayjs(at).format("YYYY-MM-DD");

/**
 * The approved assessments a young person's history may show, oldest first:
 * those this session may read, on an instrument whose name parses.
 */
export const approvedOf = ({
  history,
  mayRead,
}: {
  history: readonly FinishedAssessment[];
  mayRead: (assessment: FinishedAssessment) => boolean;
}): FinishedAssessment[] =>
  history
    .filter(
      (assessment) =>
        assessment.status === "approved" &&
        familyOfName(assessment.instrument.name) !== undefined &&
        mayRead(assessment),
    )
    .toSorted(
      (left, right) => left.finishedAt.getTime() - right.finishedAt.getTime(),
    );

/** Each family the approved assessments, oldest first as `approvedOf` gives them, are on, the most recently assessed first. */
export const familiesOf = (
  approved: readonly FinishedAssessment[],
): HistoryFamily[] => {
  const families = new Map<string, HistoryFamily>();

  for (const assessment of approved) {
    const family = familyOfName(assessment.instrument.name);

    if (family !== undefined) {
      const known = families.get(family);

      families.set(family, {
        family,
        count: (known?.count ?? 0) + 1,
        latest: assessment.finishedAt,
      });
    }
  }

  return [...families.values()].toSorted(
    (left, right) => right.latest.getTime() - left.latest.getTime(),
  );
};

/** The assessments between two days, both included, either end open where it is not named. */
export const withinRange = <T extends { finishedAt: Date }>({
  assessments,
  from,
  to,
}: {
  assessments: readonly T[];
  from?: string | undefined;
  to?: string | undefined;
}): T[] =>
  assessments.filter(({ finishedAt }) => {
    const day = dayOf(finishedAt);

    return (
      (from === undefined || day >= from) && (to === undefined || day <= to)
    );
  });

/**
 * The first day of the year up to `to`: the day of the earliest assessment
 * no more than a year before it, `days` being oldest first.
 */
export const yearBefore = ({
  days,
  to,
}: {
  days: readonly string[];
  to: string;
}): string | undefined => {
  const since = dayjs(to).subtract(1, "year").format("YYYY-MM-DD");

  return days.find((day) => day >= since && day <= to);
};

/**
 * The range a view names, which runs to its `to`, or to the latest where it
 * names none, and from its `from`, or from a year before its end where it
 * names none.
 */
export const rangeOf = ({
  days,
  from,
  to,
}: {
  days: readonly string[];
  from?: string | undefined;
  to?: string | undefined;
}): { from?: string | undefined; to?: string | undefined } => {
  const end = to ?? days.at(-1);

  return {
    from:
      from ??
      (end === undefined
        ? undefined
        : dayjs(end).subtract(1, "year").format("YYYY-MM-DD")),
    to,
  };
};

/** How many items an assessment's summary shows in each charted column, given every item it rated. */
export const countsOf = (
  rated: readonly RatedItem[],
): Readonly<Record<Charted, number>> => {
  const counts: Record<Charted, number> = {
    needs: 0,
    strengths: 0,
    history: 0,
  };

  for (const { category, rating } of rated) {
    if (category !== "caregiver" && showsInSummary({ category, rating })) {
      counts[category] += 1;
    }
  }

  return counts;
};

const acrossOf = (
  category: GroupCategory,
  ratings: readonly (number | undefined)[],
): AcrossRange | undefined => {
  const latest = ratings.at(-1);
  const earlier = ratings.slice(0, -1);

  if (earlier.length === 0) {
    return undefined;
  }

  // Nothing can be said of how an item went that the latest does not rate.
  if (latest === undefined) {
    return "not_rated";
  }

  const shown = (rating: number | undefined) =>
    rating !== undefined && showsInSummary({ category, rating });
  const shownBefore = earlier.some(shown);
  const same = ratings.every((rating) => rating === latest);

  return match(category)
    .with("history", (): AcrossRange => {
      if (same) {
        return "same";
      }

      return shown(latest) && !shownBefore ? "new" : "changed";
    })
    .with("strengths", (): AcrossRange => {
      if (shown(latest)) {
        return "still_to_build";
      }

      if (shownBefore) {
        return "built";
      }

      return same ? "same" : "changed";
    })
    .with("needs", "caregiver", (): AcrossRange => {
      if (ratings.every(shown)) {
        return "persistent";
      }

      if (shown(latest)) {
        return shownBefore ? "recurring" : "new";
      }

      if (shownBefore) {
        return "resolved";
      }

      return same ? "same" : "changed";
    })
    .exhaustive();
};

/** Where each kind of domain's rows come: the history first, as context. */
const GROUP_ORDER: Record<GroupCategory, number> = {
  history: 0,
  needs: 1,
  strengths: 2,
  caregiver: 3,
};

/**
 * The rows of the history's table, in groups: what happened first, then the
 * needs domain by domain, then the strengths and the caregiver's needs.
 *
 * An item is matched across assessments by its key, which means one thing
 * throughout a family, and is titled and placed as the latest assessment to
 * rate it asks it. `current` lists what the latest assessment's summary shows,
 * and `historic` what any assessment's in the range showed.
 */
export const rowsOf = ({
  points,
  items,
}: {
  points: readonly HistoryPoint[];
  items: HistoryItems;
}): HistoryRowGroup[] => {
  const latestRated = new Map<string, RatedItem>();

  for (const point of points) {
    for (const rated of point.rated) {
      latestRated.set(rated.item, rated);
    }
  }

  const ratingsOf = (item: string) =>
    points.map(({ rated }) => rated.find((each) => each.item === item)?.rating);
  const latestPoint = points.at(-1);
  const listed = (entry: RatedItem): boolean => {
    const shown = (rating: number | undefined) =>
      rating !== undefined &&
      showsInSummary({ category: entry.category, rating });

    return match(items)
      .with("all", () => true)
      .with("historic", () => ratingsOf(entry.item).some(shown))
      .with("current", () =>
        shown(
          latestPoint?.rated.find(({ item }) => item === entry.item)?.rating,
        ),
      )
      .exhaustive();
  };

  // Each with where its first item comes in our form, which orders the groups.
  const groups = new Map<
    string,
    { group: HistoryRowGroup; position: number }
  >();

  for (const entry of [...latestRated.values()].toSorted(
    (left, right) => left.position - right.position,
  )) {
    if (listed(entry)) {
      const key =
        entry.category === "needs" ? `needs/${entry.group}` : entry.category;
      const known = groups.get(key);
      const group = known?.group ?? {
        key,
        category: entry.category,
        domain: entry.category === "needs" ? entry.domain : undefined,
        rows: [],
      };
      const ratings = ratingsOf(entry.item);

      group.rows.push({
        item: entry.item,
        title: entry.title,
        category: entry.category,
        scale: entry.scale,
        ratings,
        across: acrossOf(entry.category, ratings),
      });
      groups.set(key, { group, position: known?.position ?? entry.position });
    }
  }

  return [...groups.values()]
    .toSorted(
      (left, right) =>
        GROUP_ORDER[left.group.category] - GROUP_ORDER[right.group.category] ||
        left.position - right.position,
    )
    .map(({ group }) => group);
};

/**
 * What happened between the first assessment in the range and the last: each
 * placement that began, and each change of assessor.
 *
 * A change of assessor is placed halfway between the assessment before it and
 * the first by the new assessor, since it happened somewhere between them.
 */
export const eventsOf = ({
  points,
  involvements,
}: {
  points: readonly HistoryPoint[];
  involvements: readonly PersonInvolvement[];
}): HistoryEvent[] => {
  const first = points[0];
  const last = points.at(-1);

  if (first === undefined || last === undefined) {
    return [];
  }

  const placements = involvements.flatMap((involvement): HistoryEvent[] =>
    involvement.kind === "placement" &&
    involvement.startedAt > first.at &&
    involvement.startedAt <= last.at
      ? [
          {
            kind: "placement",
            at: involvement.startedAt,
            name: involvement.party.name,
          },
        ]
      : [],
  );
  const assessors = points.flatMap((point, index): HistoryEvent[] => {
    const before = points[index - 1];

    return before === undefined || before.assessor === point.assessor
      ? []
      : [
          {
            kind: "assessor",
            at: new Date((before.at.getTime() + point.at.getTime()) / 2),
            name: point.assessor,
          },
        ];
  });

  return [...placements, ...assessors].toSorted(
    (left, right) => left.at.getTime() - right.at.getTime(),
  );
};

/** How many assessments the chart and table show at a time. */
export const WINDOW_SIZE = 8;

/**
 * The assessments the chart and table show, as indices into the range, both
 * ends included: `WINDOW_SIZE` of them ending at the last on or before
 * `endingOn`, or at the latest where no day is named or none is that early.
 */
export const windowOf = ({
  days,
  endingOn,
}: {
  days: readonly string[];
  endingOn: string | undefined;
}): { from: number; to: number } => {
  const named =
    endingOn === undefined ? -1 : days.findLastIndex((day) => day <= endingOn);
  const to = named === -1 ? days.length - 1 : named;

  return { from: Math.max(0, to - WINDOW_SIZE + 1), to };
};
